import * as R from "ramda";

import { arrayOf, bool, number, shape, string } from "prop-types";
import {
  createFacetString,
  createPageString,
  initFacetsFromQs,
  updateFacets,
  updateQueryParams,
  updateResponse,
} from "../../common/helpers.js";
import { useEffect, useRef, useState } from "react";

import CallToAction from "../CallToAction/CallToAction.jsx";
import { SearchBar } from "./SearchBar.jsx";
import { FacetBubbles } from "./FacetBubbles.jsx";
import Facets from "./Facets.jsx";
import Loader from "../Loader/Loader.jsx";
import VariantOne from "./VariantOne.jsx";
import VariantThree from "./VariantThree.jsx";
import VariantTwo from "./VariantTwo.jsx";
import VariantFive from "./VariantFive.jsx";
import {
  mockApiResponse,
  mockApiResponseNoImages,
  mockApiResponseVariantFive,
  mockApiResponseVariantTwo,
  mockApiResponseVariantThree,
} from "./mockData.js";
import cx from "classnames";

const propTypes = {
  storybook: bool,
  index: number,
  dataModel: shape({
    url: string.isRequired,
    loadMore: bool.isRequired,
    loadMoreText: string.isRequired,
    perPage: number.isRequired,
    title: string,
    facetKeys: arrayOf(string),
    showFacetsLabel: string.isRequired,
    cta: shape({ href: string, text: string, target: string }),
    featuredPromoItems: shape({}),
  }).isRequired,
  dataQuery: shape({
    pageId: string.isRequired,
    listingId: string.isRequired,
    showSummaries: bool,
    showReadTimes: bool,
    showEyebrow: bool,
    showImages: bool,
    variantType: number,
  }).isRequired,
};

const DynamicContentListing = (props) => {
  const {
    storybook,
    index,
    dataModel: {
      url,
      loadMore,
      loadMoreText,
      title,
      cta,
      facetKeys,
      showFacetsLabel,
      featuredPromoItems,
    },
    dataQuery: {
      pageId,
      listingId,
      showSummaries,
      showReadTimes,
      showEyebrow,
      showImages,
      variantType,
    },
  } = props;

  const [response, setResponse] = useState({});

  const [activeFacets, setActiveFacets] = useState({
    ...initFacetsFromQs(facetKeys),
    page: 1,
  });

  const [facetMenuOpen, setFacetMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const facetStringRef = useRef();

  const loadedResultsRef = useRef(0);

  // Handle special case where we may have a search term coming from global search
  const initialUrl = new URL(window.location.href);
  const initialParams = new URLSearchParams(initialUrl.search);
  const initialQueryParam = initialParams.get("q");

  // Used to keep track of entered search term
  const [searchTerm, setSearchTerm] = useState(initialQueryParam ?? "");
  const [prevSearchTerm, setPrevSearchTerm] = useState("");
  const searchRef = useRef(null);

  const hideLoadMore =
    Object.keys(response).length === 0 ||
    loadedResultsRef.current === response?.totalResults;

  useEffect(() => {
    setIsLoading(true);

    const searchString = searchTerm ? "&q=" + searchTerm : "";
    const facetString = createFacetString(activeFacets) + searchString;
    const pageString = createPageString(activeFacets);

    if (storybook === true) {
      // Pull the results from our mock API data
      const apiResponse =
        variantType === 5
          ? mockApiResponseVariantFive
          : variantType === 3
          ? mockApiResponseVariantThree
          : variantType === 2
          ? mockApiResponseVariantTwo
          : showImages === false
          ? mockApiResponseNoImages
          : mockApiResponse;

      setResponse((prevState) =>
        R.mergeDeepWithKey(
          (k, l, r) => (k === "results" ? R.concat(l, r) : r),
          prevState,
          apiResponse,
        ),
      );

      facetStringRef.current = facetString;
      updateQueryParams(facetKeys, activeFacets);
      setIsLoading(false);
    } else {
      // Fetch the results from the API
      fetch(
        `${url}?listingId=${listingId}&pageId=${pageId}&showEyebrow=${showEyebrow}&showSummaries=${showSummaries}&showReadTimes=${showReadTimes}&showImages=${showImages}${
          facetString ? facetString : ""
        }&${pageString}&variantType=${variantType}&${searchString}`,
      )
        .then((response) => response.json())
        .then((data) => {
          // Update the amount of non-featured results loaded so far;
          // needed for hideLoadMore logic.
          // If the search term has changed, reset the number of loaded results.
          if (searchTerm !== prevSearchTerm) {
            loadedResultsRef.current = data.results.length;
          } else {
            // Otherwise, keep adding on each time a new page is loaded.
            loadedResultsRef.current = loadedResultsRef.current +=
              data.results.length;
          }

          setPrevSearchTerm(searchRef.current?.value);

          // Variant two has special logic to allow for
          // a featured news story to be spliced into the results.
          if (variantType === 2 && featuredPromoItems) {
            if (data.results.length >= 12) {
              let resultsPlaced = 0;
              let featureCounter = 1;
              let featureIndex = 0;

              data.results.forEach((result, i) => {
                resultsPlaced++;

                // Place a featured item after 12 results have been placed
                if (resultsPlaced === 12) {
                  data.results.splice(
                    i + featureCounter,
                    0,
                    featuredPromoItems[featureIndex],
                  );

                  resultsPlaced = 0;
                  featureCounter += 1;

                  // If there are more featuredPromoItems, increment the feature index;
                  // otherwise, reset it to 0
                  if (featureIndex < featuredPromoItems.length - 1) {
                    featureIndex += 1;
                  } else {
                    featureIndex = 0;
                  }
                }
              });
            }
          }

          setResponse((prevState) =>
            updateResponse(facetString, facetStringRef, data, prevState),
          );
        })
        .catch((e) => console.warn(e))
        .finally(() => {
          facetStringRef.current = facetString;
          updateQueryParams(facetKeys, activeFacets, true, searchTerm);
          setIsLoading(false);
        });
    }
  }, [
    url,
    listingId,
    pageId,
    showEyebrow,
    showSummaries,
    showReadTimes,
    loadMore,
    showImages,
    activeFacets,
    facetKeys,
    variantType,
    storybook,
    featuredPromoItems,
    searchTerm,
  ]);

  const handlePageChange = (page) =>
    setActiveFacets((prev) => R.evolve({ ["page"]: () => page }, prev));

  const handleFacetClick = (facetId, valueId) => {
    // First, reset number of loaded results
    loadedResultsRef.current = 0;

    return setActiveFacets((prev) => updateFacets(facetId, valueId, prev));
  };

  const clearAllFacets = () => {
    setActiveFacets({ page: 1 });
  };

  // Handles when users press the enter key in the search box
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchRef.current.value !== searchTerm
        ? [clearAllFacets(), setSearchTerm(searchRef.current.value)]
        : null;
    }
  };

  return (
    <section className="dynamic-content-listing">
      <div className="container--100">
        {title && (
          <div className="container--100">
            <h2 className="dynamic-content-listing__title h2">{title}</h2>
          </div>
        )}

        {variantType === 2 && (
          <SearchBar
            includeSearchBar
            searchPlaceholder="Search"
            searchRef={searchRef}
            handleKeyDown={handleKeyDown}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchIcon={searchIcon}
            clearAllFacets={clearAllFacets}
          />
        )}

        {/* Variant two will have its own special search and filtering code */}
        {variantType !== 2 && (
          <>
            <div className="dynamic-content-listing__facets">
              {response?.facets && (
                <Facets
                  facets={response.facets}
                  facetMenuOpen={facetMenuOpen}
                  setFacetMenuOpen={setFacetMenuOpen}
                  onFacetClick={handleFacetClick}
                  showFacetsLabel={showFacetsLabel}
                />
              )}
            </div>

            <FacetBubbles
              clearAllFacets={clearAllFacets}
              onFacetClick={handleFacetClick}
              totalResults={response?.totalResults}
              setFacetMenuOpen={setFacetMenuOpen}
              facetMenuOpen={facetMenuOpen}
              activeFacets={R.compose(
                R.when(
                  (obj) => Object.keys(obj).length === 0,
                  () => null,
                ),
                R.pickBy((val, key) => key !== "page"),
              )(activeFacets)}
            />
          </>
        )}

        <div className="container--100 dynamic-content-listing__results">
          {variantType === 1 && <VariantOne results={response?.results} />}
          {variantType === 2 && (
            <VariantTwo
              response={response}
              results={response?.results}
              facets={response?.facets}
              handleFacetClick={handleFacetClick}
              clearAllFacets={clearAllFacets}
              activeFacets={activeFacets}
              setActiveFacets={setActiveFacets}
            />
          )}
          {variantType === 3 && <VariantThree results={response?.results} />}
          {variantType === 5 && (
            <VariantFive results={response?.results} index={index} />
          )}
        </div>

        {isLoading && <Loader />}

        {(cta || loadMore) && (
          <div
            className={cx("container dynamic-content-listing__cta", {
              "dynamic-content-listing__cta--variant-one": variantType === 1,
              "dynamic-content-listing__cta--variant-five": variantType === 5,
            })}
          >
            {cta && !loadMore && (
              <CallToAction
                className="call-to-action--outline"
                href={cta?.href}
                target={cta?.target}
              >
                {cta.text}
              </CallToAction>
            )}

            {loadMore && !hideLoadMore && !cta && (
              <button
                className="call-to-action call-to-action--outline"
                onClick={() => {
                  handlePageChange(activeFacets.page + 1);
                }}
                type="button"
              >
                {loadMoreText}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// Specifies the default values for props:
DynamicContentListing.defaultProps = {
  storybook: false,
};

DynamicContentListing.propTypes = propTypes;
export default DynamicContentListing;

const searchIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="feather feather-search"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);
